import { useState } from "react";
import { Lock, Loader2, AlertCircle } from "lucide-react";
import { api } from "@/lib/site";

export default function CI62Gate({ onUnlock }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (loading || !password) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/ci62/verify", { password });
      if (data?.valid) {
        sessionStorage.setItem("ci62_unlocked", "1");
        onUnlock();
      } else {
        setError("Incorrect password. Please try again.");
        setPassword("");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Unable to verify. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="ci62-gate"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-[#0A1628] px-4"
    >
      <div className="absolute inset-0 bg-lab-grid opacity-[0.08]" aria-hidden />
      <div className="relative w-full max-w-md">
        <div className="flex flex-col items-center text-center mb-8">
          <span className="inline-flex items-center justify-center w-14 h-14 border border-teal-500/40 text-teal-400 mb-6">
            <Lock className="w-6 h-6" strokeWidth={1.5} />
          </span>
          <h1 className="font-pub text-3xl font-semibold text-white tracking-tight">
            Restricted Access
          </h1>
          <p className="mt-3 text-slate-400 text-sm leading-relaxed max-w-sm">
            This page contains confidential product information. Please enter the access password provided to you.
          </p>
        </div>

        <form onSubmit={submit} noValidate className="space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-[0.15em] text-slate-400 mb-2 font-medium">
              Access Password
            </label>
            <input
              data-testid="ci62-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
              placeholder="Enter password"
              className="w-full bg-[#0F1F38] border border-slate-700 text-white px-4 h-12 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/30 transition-colors placeholder:text-slate-500"
            />
          </div>

          {error && (
            <div
              data-testid="ci62-gate-error"
              className="flex items-start gap-2 text-sm text-red-400 bg-red-500/10 border border-red-500/20 px-4 py-3"
            >
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={2} />
              <span>{error}</span>
            </div>
          )}

          <button
            type="submit"
            data-testid="ci62-gate-submit"
            disabled={loading}
            className="w-full inline-flex items-center justify-center bg-teal-600 hover:bg-teal-500 text-white h-12 text-sm font-medium tracking-wide transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 w-4 h-4 animate-spin" strokeWidth={2} />
                Verifying...
              </>
            ) : (
              "Unlock Page"
            )}
          </button>
        </form>

        <p className="mt-8 text-center text-xs text-slate-600">
          Xpertee Pharma Services Private Limited
        </p>
      </div>
    </div>
  );
}
